/**
 * Paddle.js loader + overlay checkout.
 *
 * Paddle is deliberately NOT bundled: the script has to come from `cdn.paddle.com` so the overlay
 * always runs Paddle's current build (their checkout, their PCI scope). It is loaded on demand —
 * the first time someone actually chooses Paddle — so an invoice list nobody pays costs nothing.
 *
 * `Paddle.Initialize` is idempotent per token, but re-running it re-registers the event callback,
 * so the loaded/initialized state is memoised at module scope rather than per component.
 */

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

declare global {
	interface Window {
		Paddle?: any;
	}
}

export interface IPaddleContext {
	clientToken: string;
	environment: string; // 'sandbox' | 'production'
}

let loader: Promise<void> | null = null;
let initializedToken: string | null = null;

/** Inject paddle.js once. Resolves when `window.Paddle` is usable. */
const loadScript = (): Promise<void> => {
	if (window.Paddle) return Promise.resolve();
	if (loader) return loader;

	loader = new Promise<void>((resolve, reject) => {
		const existing = document.querySelector<HTMLScriptElement>(
			`script[src="${PADDLE_JS_URL}"]`
		);

		const onError = () => {
			// Reset so a later attempt can retry — a blocked CDN is often transient (ad blocker
			// toggled off, network recovered), and a permanently rejected promise would make the
			// button dead for the rest of the session.
			loader = null;
			reject(new Error('Paddle checkout could not load. Please try another payment method.'));
		};

		if (existing) {
			existing.addEventListener('load', () => resolve());
			existing.addEventListener('error', onError);
			return;
		}

		const script = document.createElement('script');
		script.src = PADDLE_JS_URL;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = onError;
		document.head.appendChild(script);
	});

	return loader;
};

/**
 * Load and initialize Paddle.js.
 *
 * @param onCompleted runs when Paddle reports `checkout.completed`. This is a UI cue ONLY — the
 *   invoice is marked paid by the webhook, never by the browser saying so.
 */
export const initPaddle = async (
	context: IPaddleContext,
	onCompleted?: () => void
): Promise<void> => {
	if (!context?.clientToken) {
		throw new Error('Paddle is not configured.');
	}

	await loadScript();

	if (initializedToken === context.clientToken) return;

	// Environment must be set BEFORE Initialize — afterwards the SDK has already resolved which
	// hosts to talk to, and a sandbox token against live hosts fails as an invalid token.
	if (context.environment === 'sandbox') {
		window.Paddle.Environment.set('sandbox');
	}

	window.Paddle.Initialize({
		token: context.clientToken,
		eventCallback: (event: any) => {
			if (event?.name === 'checkout.completed') onCompleted?.();
		},
	});

	initializedToken = context.clientToken;
};

/** Open the overlay on an existing Paddle transaction. */
export const openPaddleCheckout = (transactionId: string): void => {
	if (!window.Paddle) {
		throw new Error('Paddle checkout is not ready.');
	}
	window.Paddle.Checkout.open({ transactionId });
};
